import { SocialAccountStatus } from '@business/entities/account';
import { ContactResponse } from '@business/entities/contact';
import { CommonService } from '@business/services';
import { PageContextService } from '@business/services/page_context_service';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { usePageInfoContext } from '@core/components/PageContext';
import { useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { NylasConnectUrl } from '@modules/account/MyAccountPage/NylasAccounts/NylasAccounts';
import { Session } from '@modules/auth/session';
import React, { Fragment, memo, useMemo } from 'react';
import { Card, CardBody, CardText, CardTitle, Col, Row } from 'reactstrap';
import { NewEmail } from './NewEmail';

export interface SendEmailProps {
  contacts: ContactResponse[];
  isCard?: boolean;
  defaultEmail?: string;
}

export const SendEmail = memo((props: SendEmailProps) => {
  const { contacts, isCard = false, defaultEmail } = props;
  const user = useObservable(Session.user$);
  const emails = useMemo(
    () =>
      R.flatMap(contacts, contact =>
        R.map(contact.emails, email => {
          return {
            email: email.value,
            name: contact.name,
          };
        }),
      ),
    [contacts],
  );

  const socialItems = useMemo(
    () =>
      CommonService.selectItems(
        (user?.socialAccounts ?? []).filter(s => s.status === SocialAccountStatus.active),
        u => u.email + `( ${u.provider} )`,
        'id',
      ),
    [user],
  );

  if (!user) return <TopLoadingBar />;

  if (socialItems.length === 0) {
    return (
      <Row>
        <Col sm="12">
          <CardTitle>Start using Full Email Sync</CardTitle>
          <CardText>
            Send emails to your contacts directly from GrandCentr.al by connecting your work email. This works with all major email providers such as
            Gmail, Yahoo!, iCloud, Microsoft Exchange, and others.
          </CardText>
          <button type="button" className="btn btn-primary waves-effect waves-light" onClick={() => NylasConnectUrl(user.id)}>
            Connect your email
          </button>
        </Col>
      </Row>
    );
  }

  const email = useMemo(() => {
    if (defaultEmail) {
      return {
        id: '0',
        subject: '',
        snippet: '',
        createdAt: 0,
        from: [],
        to: [{ email: defaultEmail, name: '' }],
        cc: [],
        bcc: [],
        threads: [],
      };
    }
    return undefined;
  }, [defaultEmail]);

  const pageContent = (
    <div className="p-2">
      <NewEmail emails={emails} toValue={defaultEmail} />
    </div>
  );

  if (isCard) {
    return (
      <Card>
        <CardBody>{pageContent}</CardBody>
      </Card>
    );
  }

  return <Fragment>{pageContent}</Fragment>;
});
